import { SxProps, Theme } from '@mui/material/styles';
import { alpha } from '@mui/material';
import Highcharts from 'highcharts';

export const chartContentStyles = {
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 400,
    gap: 2,
  } as SxProps<Theme>,

  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 400,
    gap: 2,
  } as SxProps<Theme>,

  retryButton: {
    mt: 1,
  } as SxProps<Theme>,

  noDataContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 400,
  } as SxProps<Theme>,

  chartContainer: {
    height: 600,
    position: 'relative',
  } as SxProps<Theme>,
};

export const chartLoadingOverlayStyles = {
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.2)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    borderRadius: 1,
  } as SxProps<Theme>,

  progressSpinner: {
    color: 'white',
    mb: 2,
  } as SxProps<Theme>,

  loadingText: {
    color: 'white',
    fontWeight: 500,
    textShadow: '0 2px 4px rgba(0,0,0,0.3)',
  } as SxProps<Theme>,
};

export const chartStatisticsStyles = {
  container: (theme: Theme) =>
    ({
      mb: 2,
      p: 1.5,
      backgroundColor: alpha(theme.palette.background.paper, 0.2),
      borderRadius: 1,
      display: 'flex',
      gap: 3,
      flexWrap: 'wrap',
      alignItems: 'center',
    }) as SxProps<Theme>,

  currentTemperature: (color: string | undefined, theme: Theme) =>
    ({
      color: color || theme.palette.success.main,
      fontWeight: 'bold',
    }) as SxProps<Theme>,

  dataPointsCaption: {
    ml: 'auto',
  } as SxProps<Theme>,

  labelStyle: (theme: Theme) => ({
    color: theme.palette.text.primary,
  }),
};

export const historicalChartModalStyles = {
  dialogPaper: {
    minHeight: 500,
  } as SxProps<Theme>,
};

export const modalHeaderStyles = {
  titleContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  } as SxProps<Theme>,

  incidentCaption: (theme: Theme) =>
    ({
      ml: 2,
      color: theme.palette.text.secondary,
    }) as SxProps<Theme>,

  buttonContainer: {
    display: 'flex',
    gap: 1,
  } as SxProps<Theme>,

  refreshButton: (theme: Theme, isFetching: boolean) =>
    ({
      color: theme.palette.primary.main,
      '&:hover': {
        backgroundColor: 'action.hover',
      },
      '& svg': {
        animation: isFetching ? 'spin 1s linear infinite' : 'none',
      },
      '@keyframes spin': {
        '0%': { transform: 'rotate(0deg)' },
        '100%': { transform: 'rotate(360deg)' },
      },
    }) as SxProps<Theme>,

  closeButton: (theme: Theme) =>
    ({
      color: theme.palette.grey[500],
    }) as SxProps<Theme>,
};

export const chartStyles = {
  plotLineLabelStyle: (color: string): Highcharts.CSSObject => ({
    color,
    fontWeight: 'bold',
    textOutline: '2px black',
  }),

  subtitleStyle: (theme: Theme): Highcharts.CSSObject => ({
    fontSize: '11px',
    color: theme.palette.text.secondary,
  }),

  axisLabelStyle: (theme: Theme): Highcharts.CSSObject => ({
    color: theme.palette.text.secondary,
  }),

  axisTitleStyle: (theme: Theme): Highcharts.CSSObject => ({
    color: theme.palette.text.primary,
  }),

  dataLabelStyle: (theme: Theme, color: string): Highcharts.CSSObject => ({
    fontSize: '12px',
    fontWeight: 'bold',
    textOutline: `2px ${theme.palette.background.paper}`,
    color,
  }),

  chartConfig: {
    height: '600px',
    backgroundColor: 'transparent',
    animation: {
      duration: 500,
    },
  },

  markerHoverState: (theme: Theme) => ({
    enabled: true,
    radius: 4,
    lineWidth: 1,
    lineColor: theme.palette.background.paper,
  }),

  scatterMarkerStyle: (theme: Theme) => ({
    enabled: true,
    radius: 10,
    lineWidth: 2,
    lineColor: theme.palette.background.paper,
  }),

  crosshairStyle: (theme: Theme) => ({
    width: 1,
    color: theme.palette.divider,
    dashStyle: 'Dot' as Highcharts.DashStyleValue,
  }),

  gridLineStyle: (theme: Theme) => ({
    color: theme.palette.divider,
    width: 0.5,
  }),

  tooltipStyle: (theme: Theme) => ({
    backgroundColor: theme.palette.background.paper,
    borderColor: theme.palette.divider,
    borderRadius: 4,
    shadow: true,
    style: {
      color: theme.palette.text.primary,
    },
  }),

  seriesAnimation: {
    duration: 250,
  },

  boostConfig: (dataLength: number) => ({
    enabled: dataLength > 5000,
    useGPUTranslations: true,
    seriesThreshold: 1,
  }),
};
